import './FormPopupSimpleItem.scss';
import { useRef, PropsWithChildren, useEffect, useState } from 'react';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import ValidationGroup from 'devextreme-react/validation-group';
import { Button, LoadIndicator } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';
import { Form, SimpleItem, RequiredRule, Label } from 'devextreme-react/form';
import { FormField } from '../../../types/types.system';
import { useAppSelector } from '../../../contexts/hooks';
import { TypeCombobox } from '../../../types/types.config';

type PopupProps = {
  title: string;
  visible: boolean;
  width?: number;
  wrapperAttr?: { class: string };
  isSaveDisabled?: boolean;
  setVisible: () => void;
  onSave?: () => void;
  loading: boolean;
  dataSource: any;
  fields: FormField[];
};

export const FormPopupSimpleItem = ({ title, visible, width = 500, setVisible, onSave, wrapperAttr = { class: '' }, isSaveDisabled = false, loading = false, dataSource, fields }: PropsWithChildren<PopupProps>) => {
  const validationGroup = useRef<ValidationGroup>(null);
  const formRef = useRef<Form>(null);
  const [Combobox, setCombobox] = useState<TypeCombobox[]>([]);
  const [forceRender, setForceRender] = useState(false);
  const lstEstudios = useAppSelector((x) => x.configuration.Data?.Estudios);

  const lstCombobox = useAppSelector((x) => x.configuration.Data?.ComboBox);

  useEffect(() => {
    if (visible) {
      setForceRender(true); // Activar renderizado seguro
      fields.forEach((field) => {
        if (field.type === 'dxDateRangeBox' && !Array.isArray(dataSource[field.equivalence])) {
          dataSource[field.equivalence] = [null, null];
        } else if (field.type === 'dxTagBox' && !Array.isArray(dataSource[field.equivalence])) {
          // El TagBox (multiselección) trabaja sobre un arreglo: con '' no pinta las selecciones
          // y el valor no llega como lista al backend.
          dataSource[field.equivalence] = [];
        } else if (!dataSource[field.equivalence]) {
          dataSource[field.equivalence] = '';
        }
      });
    }
  }, [visible, fields, dataSource]);

  useEffect(() => {
    // Sólo procesar campos que tengan Combobox definido (no null/empty)
    fields
      .filter((field) => field.Combobox != null && (typeof field.Combobox !== 'string' || field.Combobox !== ''))
      .forEach((element) => {
        // Convertir el ID del combobox a número de forma segura.
        const comboboxId = Number(element?.Combobox);
        if (!isNaN(comboboxId)) {
          const combobox = lstCombobox?.find((x) => x.Id === comboboxId);          
          if (combobox) {
            setCombobox((prev) => [...prev, combobox]);
          }
        }
      });
  }, [fields, lstCombobox]);

  const handleClose = () => {
    setForceRender(false); // Desmontar formulario
    setVisible();
  };

  const onSaveClick = () => {
    const formInstance = formRef.current?.instance;

    if (formInstance) {
      const validationResult = formInstance.validate();
      if (!validationResult.isValid) {
        console.warn('Errores de validación:', validationResult?.brokenRules);
        return;
      }
    }

    onSave && onSave();
  };

  return (
    <Popup title={title} visible={visible} onHidden={handleClose} hideOnOutsideClick={true} showCloseButton={true} wrapperAttr={{ ...wrapperAttr, class: `${wrapperAttr?.class} form-popup` }} width={width} height='auto'>
      <ToolbarItem toolbar='bottom' location='center'>
        <div className={`form-popup-buttons-container ${width <= 360 ? 'flex-buttons' : ''}`}>
          <Button stylingMode='contained' type={loading ? 'normal' : 'default'} disabled={isSaveDisabled} onClick={onSaveClick}>
            <div style={{ padding: 8 }}>
              <span className='dx-button-text' style={{ marginRight: loading ? 10 : 0 }}>
                {loading ? formatMessage('SavingButton') : formatMessage('saveButton')}
              </span>
              <LoadIndicator className='button-indicator' height={15} width={15} style={{ marginLeft: 20 }} visible={loading} />
            </div>
          </Button>
        </div>
      </ToolbarItem>
      {forceRender && (
        <ValidationGroup ref={validationGroup}>
          <Form ref={formRef} formData={dataSource} showValidationSummary={true} showRequiredMark={true}>
            {fields.map((field) => {
              // Soporte especial para combobox 'Estudios'
              let items = undefined as any;
              let displayExpr: any = undefined;
              let valueExpr: any = undefined;

              if (String(field.Combobox) === 'Estudios') {
                items = lstEstudios;
                displayExpr = (item: any) => (item ? `${item.Es_Codigo} - ${item.Es_Nombre}` : '');
                valueExpr = 'Es_Id';
              } else {
                console.log(Combobox)
                console.log(field)
                // Comparar por número (field.Combobox puede venir como string)
                const currentCombobox = Combobox.find((x) => x.Id === Number(field.Combobox));
                items = field.Combobox ? currentCombobox?.Data : undefined;
                displayExpr = currentCombobox?.CampoVisible || 'Name';
                valueExpr = currentCombobox?.CampoId || 'Id';
              }

              return (
                <SimpleItem
                  key={field.equivalence}
                  dataField={field.equivalence}
                  editorType={field.type}
                  editorOptions={{
                    placeholder: field.label,
                    mask: field.mask || undefined,
                    min: field.min,
                    max: field.max,
                    items: items,
                    displayExpr: displayExpr,
                    valueExpr: valueExpr,
                    value: dataSource[field.equivalence],
                    // Enable search/filtering in combo/select editors
                    searchEnabled: true,
                    // Use the same displayExpr for searching; if it's a function, pass it through
                    searchExpr: displayExpr,
                    searchMode: 'contains',
                    showClearButton: true,
                    // Keep validation UI configuration if needed
                    isValid: true,
                    validationMessageMode: 'none',
                    // Multiselección. En una sola línea los nombres largos (contratos, EPS) se
                    // recortaban y no se alcanzaba a ver qué había seleccionado, así que las
                    // etiquetas se envuelven. Las casillas dejan marcar varios sin que el
                    // desplegable se cierre en cada clic, y habilitan el "Seleccionar todo".
                    ...(field.type === 'dxTagBox'
                      ? {
                          multiline: true,
                          showSelectionControls: true,
                          selectAllMode: 'allPages',
                          applyValueMode: 'instantly',
                          // A partir de 10 se resume con un contador para que el cuadro no
                          // crezca sin límite y tape el resto de los filtros.
                          maxDisplayedTags: 10,
                          showMultiTagOnly: false,
                          showDropDownButton: true,
                        }
                      : {}),
                  }}>
                  <Label text={field.label} />
                  {field.required && <RequiredRule message={`${field.label} es requerido`} />}
                </SimpleItem>
              );
            })}
          </Form>
        </ValidationGroup>
      )}
    </Popup>
  );
};
